import ResourceTyped from 'Resources/ResourceTyped';

// Playbook (agent workflow) compile/save/view resource, split from the
// Assistant god resource (WEB-7958). Same URLs — pure client-side reorg.

/**
 * Mirror of the backend `CompiledWorkflowDraft` shape (see
 * `base/src/core/services/ai/agent/workflow/compiler/llmCompiler.types.ts`)
 * with only the fields the read-only review modal needs. Backend types use
 * `unknown` for `args` and similar — we keep that loose here so the modal
 * can render whatever the compiler produced without lying about shape.
 */
export type CompiledPlaybookStep =
	| { kind: 'tool'; id: string; tool: string; args: unknown; bind?: string; onError?: string }
	| { kind: 'llm'; id: string; prompt: unknown; output: unknown; bind: string; onError?: string }
	| { kind: 'if'; id: string; cond: string; then: string[]; else?: string[] }
	| { kind: 'foreach'; id: string; over: string; as: string; body: string[]; bind?: string };

export type CompiledPlaybookArgDecision =
	| { kind: 'keep-constant'; rationale: string }
	| { kind: 'keep-binding'; rationale: string }
	| { kind: 'promote-template'; template: string; rationale: string }
	| { kind: 'promote-llm-step'; promotedStepId: string; rationale: string };

export type CompiledPlaybookWarning = {
	code: string;
	stepId?: string;
	argPath?: string;
	path?: string;
	message: string;
};

export type CompiledPlaybookDraft = {
	sourceRunId: number;
	workflow: {
		trigger: { kind: string; [k: string]: unknown };
		steps: CompiledPlaybookStep[];
	};
	provenance: Record<
		string,
		{
			sourceEventSeq: number;
			args: Record<
				string,
				{ kind: 'constant' | 'binding-step' | 'binding-trigger' | 'generated'; [k: string]: unknown }
			>;
		}
	>;
	warnings: CompiledPlaybookWarning[];
	validationIssues: Array<{ message: string; path?: string }>;
	decisions: Record<string, CompiledPlaybookArgDecision>;
	compilerWarnings: CompiledPlaybookWarning[];
	compilerVersion: { model: string; promptHash: string };
	/** Per-step curated name + description from the tool registry (presentation only). */
	stepMeta?: Record<string, { name: string; description: string }>;
	/** Per-step LLM-generated action title + context-rich description (presentation only). */
	stepSummaries?: Record<string, { title: string; description: string }>;
	/**
	 * Cross-run consensus summary (Phase 2). Present when compiled from a routine.
	 * `confident: false` means the routine's recent runs disagreed on structure —
	 * a signal it may work better as an agent routine.
	 */
	runConsistency?: {
		runsAnalyzed: number;
		cohortSize: number;
		signature: string[];
		confident: boolean;
	};
	/**
	 * Average per-run cost of the routine as an agent (`before`) vs as a compiled
	 * Playbook (`after`), for the credit-saving comparison (WEB-7810). `after` is
	 * null until the routine has run as a Playbook at least once.
	 */
	costComparison?: {
		before: { avgCredits: number; avgCostCents: number; runs: number } | null;
		after: { avgCredits: number; avgCostCents: number; runs: number } | null;
		/** True when `after` is projected from the draft's LLM steps rather than
		 *  measured from real Playbook runs (WEB-7816). */
		afterEstimated?: boolean;
	};
};

/**
 * A rejected workflow call. `fromServer` says whether `message` is the
 * explanation the API wrote for the user ("every recent run of this routine
 * delegated its work…") or our own last-resort string — the drawer shows the
 * former verbatim and falls back to a friendlier translated line for the
 * latter, which is what stops a status code being presented as advice.
 */
export class WorkflowRequestError extends Error {
	readonly fromServer: boolean;
	readonly status: number;
	constructor(message: string, fromServer: boolean, status: number) {
		super(message);
		this.name = 'WorkflowRequestError';
		this.fromServer = fromServer;
		this.status = status;
	}
}

// Every workflow endpoint speaks the same dialect: POST json, `{error, data}`
// envelope, user-readable error message on non-2xx.
const postWorkflow = async <T>(path: string, payload: unknown, fallback: string): Promise<T> => {
	const res = await fetch(`/api/v2/agentWorkflows/${path}`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload)
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) {
		// The v2 envelope names this field `msg`, not `message` — reading the
		// wrong one silently swallowed every explanation the controllers write
		// (why a routine can't be compiled, why it needs a run first) and left
		// the caller with "Compile request failed (403)". `translated` first,
		// matching AgentApprovals / AgentContext / AgentConnections.
		const serverMessage =
			(typeof body?.error?.translated === 'string' && body.error.translated) ||
			(typeof body?.error?.msg === 'string' && body.error.msg) ||
			(typeof body?.error?.message === 'string' && body.error.message) ||
			(typeof body?.error === 'string' && body.error) ||
			null;
		throw new WorkflowRequestError(
			serverMessage || `${fallback} (${res.status})`,
			Boolean(serverMessage),
			res.status
		);
	}
	return (body?.data ?? body) as T;
};

/** The user-readable half of a failure, or null when only a status came back. */
export type PlaybookCandidacy = {
	eligible: boolean;
	/** Succeeded agent runs actually compared. */
	runsAnalyzed: number;
	/** Workflow-capable tool calls in the shared signature; 0 when they disagree. */
	toolCount: number;
	reason?: 'alreadyBound' | 'notEnoughRuns' | 'inconsistent' | 'noToolSteps';
};

export const serverMessageOf = (err: unknown): string | null =>
	err instanceof WorkflowRequestError && err.fromServer ? err.message : null;

class AssistantWorkflows extends ResourceTyped<CompiledPlaybookDraft> {
	constructor() {
		super('agentWorkflows');
	}

	/**
	 * Compile a Playbook draft from the routine's most recent succeeded run.
	 * Returns the raw `CompiledPlaybookDraft` for read-only review (no DB write
	 * — the visual editor is what persists). Throws with a user-readable
	 * message when the routine has no succeeded run yet.
	 */
	compilePlaybookFromRoutine(routineId: number): Promise<CompiledPlaybookDraft> {
		return postWorkflow('compile-from-routine', { routineId }, 'Compile request failed');
	}

	/**
	 * Persist a compiled Playbook draft (WEB-6850 / WEB-6857).
	 *
	 * Two modes:
	 * - **Routine-bound** (`routineId: number`) — also binds the routine's
	 *   `workflowId` so its next trigger dispatches the workflow runner
	 *   instead of the agent prompt path.
	 * - **Standalone** (`routineId: null`) — just persists the `agent_workflow`
	 *   row; the user wires a trigger later. Permission falls back to source
	 *   run ownership on the backend.
	 */
	savePlaybookFromCompiled(
		routineId: number | null,
		draft: CompiledPlaybookDraft,
		name?: string
	): Promise<{ workflowId: number }> {
		const trimmedName = name && name.trim().length > 0 ? name.trim() : undefined;
		const payload = {
			...(routineId == null ? {} : { routineId }),
			...(trimmedName ? { name: trimmedName } : {}),
			draft
		};
		return postWorkflow('save-from-compiled', payload, 'Save request failed');
	}

	/**
	 * Unbind the routine's Playbook so it reverts to the agent-prompt path on
	 * the next trigger. The previous `agent_workflow` row stays in the DB as
	 * an audit trail — only `routine.workflowId` is cleared.
	 */
	async unsetPlaybookFromRoutine(routineId: number): Promise<void> {
		await postWorkflow('unset-from-routine', { routineId }, 'Unset request failed');
	}

	/**
	 * Recompile the routine's Playbook from its latest succeeded run. Creates
	 * a fresh `agent_workflow` row and rebinds the routine to it; the previous
	 * workflow row stays in the DB for audit. Returns the new workflow id and
	 * the just-saved draft so the UI can show it in a read-only review modal
	 * without a second round-trip.
	 */
	recompilePlaybookFromRoutine(routineId: number): Promise<{ workflowId: number; draft: CompiledPlaybookDraft }> {
		return postWorkflow('recompile-from-routine', { routineId }, 'Recompile request failed');
	}

	/**
	 * Read-only view of a routine's saved Playbook — returns the stored
	 * CompiledPlaybookDraft with no recompile/LLM call/mutation (WEB-7810).
	 */
	viewPlaybookFromRoutine(routineId: number): Promise<CompiledPlaybookDraft> {
		return postWorkflow('view-from-routine', { routineId }, 'View request failed');
	}

	/**
	 * Would this routine make a good Playbook? (WEB-10287)
	 *
	 * Cheap enough to call on a view: the backend compares tool names across the
	 * routine's recent runs and neither extracts nor compiles. An ordinary "no"
	 * comes back as `eligible: false` with a reason, not an error.
	 */
	playbookCandidacy(routineId: number): Promise<PlaybookCandidacy> {
		return postWorkflow('playbook-candidacy', { routineId }, 'Candidacy request failed');
	}
}

const resource = new AssistantWorkflows();

export default resource;
